"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CountIf = exports.Countall = exports.Counta = exports.Count = exports.ArrayCompact = exports.ArrayFlatten = exports.ArrayUnique = exports.ArrayJoin = exports.flattenParams = void 0;
const basic_1 = require("./basic");
const types_1 = require("../../types");
const model_1 = require("../../model");
const dayjs_1 = __importDefault(require("dayjs"));
const i18n_1 = require("../../exports/i18n");
class ArrayFunc extends basic_1.FormulaFunc {
}
ArrayFunc.type = types_1.FormulaFuncType.Array;
ArrayFunc.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
function isArrayParam(params) {
    if (params.length !== 1) {
        return false;
    }
    if (params[0].node.valueType === types_1.BasicValueType.Array) {
        return true;
    }
    return false;
}
/**
 * Flatten the value of the parameter
 */
const flattenParams = (params) => {
    const value = params.reduce((prev, cur) => {
        const { value, node } = cur;
        if (cur.node.valueType === types_1.BasicValueType.Array) {
            const { field, context } = node;
            if (model_1.Field.bindContext(field, context.state).isComputed) {
                const v = model_1.Field.bindContext(field, context.state).arrayValueToArrayStringValueArray(value);
                return v != null ? [...prev, v] : prev;
            }
        }
        return value != null ? [...prev, value] : prev;
    }, []);
    return value.flat(Infinity);
};
exports.flattenParams = flattenParams;
var SymbolType;
(function (SymbolType) {
    SymbolType["Equal"] = "Equal";
    SymbolType["NotEqual"] = "NotEqual";
    SymbolType["Greater"] = "Greater";
    SymbolType["Less"] = "Less";
})(SymbolType || (SymbolType = {}));
class ArrayJoin extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.String;
    }
    static func([valuesParam, separatorParam]) {
        const { value, node } = valuesParam;
        const separator = (separatorParam && separatorParam.value) || ', ';
        if (node.valueType === types_1.BasicValueType.Array) {
            const { field, context } = node;
            if (model_1.Field.bindContext(field, context.state).isComputed) {
                const v = model_1.Field.bindContext(field, context.state).arrayValueToArrayStringValueArray(value);
                return v === null || v === void 0 ? void 0 : v.join(separator);
            }
            return value === null || value === void 0 ? void 0 : value.join(separator);
        }
        return value;
    }
}
exports.ArrayJoin = ArrayJoin;
class ArrayUnique extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Array;
    }
    static func(params) {
        const flattenValue = (0, exports.flattenParams)(params);
        return [...new Set(flattenValue)];
    }
}
exports.ArrayUnique = ArrayUnique;
class ArrayFlatten extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Array;
    }
    static func(params) {
        return (0, exports.flattenParams)(params);
    }
}
exports.ArrayFlatten = ArrayFlatten;
class ArrayCompact extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Array;
    }
    static func(params) {
        const flattenValue = (0, exports.flattenParams)(params);
        return flattenValue.filter(v => v !== '');
    }
}
exports.ArrayCompact = ArrayCompact;
class Count extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        const calc = (v) => typeof v === 'number' && !isNaN(v);
        if (isArrayParam(params)) {
            const innerValueType = params[0].node.innerValueType;
            if (innerValueType && innerValueType === types_1.BasicValueType.DateTime) {
                return 0;
            }
            if (!params[0].value) {
                return 0;
            }
            return params[0].value.filter(calc).length;
        }
        return params.filter(param => calc(param.value)).length;
    }
}
exports.Count = Count;
Count.acceptValueType = new Set([types_1.BasicValueType.Array, ...basic_1.FormulaFunc.acceptValueType]);
class Counta extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        // Due to how other platforms handle this function, false is also included in the range of nulls
        const calc = (v) => v != null && v !== '' && v !== false;
        if (isArrayParam(params)) {
            if (!params[0].value) {
                return 0;
            }
            return params[0].value.filter(calc).length;
        }
        return params.filter(param => calc(param.value)).length;
    }
}
exports.Counta = Counta;
class Countall extends ArrayFunc {
    static validateParams(_params) {
        //
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        if (isArrayParam(params)) {
            if (params[0].value == null) {
                return 0;
            }
            return params[0].value.length;
        }
        return params.length;
    }
}
exports.Countall = Countall;
class CountIf extends ArrayFunc {
    static validateParams(params) {
        if (params.length < 2) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.function_validate_params_count_at_least, {
                name: 'COUNTIF',
                count: 2,
            }));
        }
    }
    static getReturnType(params) {
        params && this.validateParams(params);
        return types_1.BasicValueType.Number;
    }
    static func(params) {
        var _a;
        const [{ node: rangeNode, value: range }, { node: conditionNode, value: condition }] = params;
        const symbol = ((_a = params[2]) === null || _a === void 0 ? void 0 : _a.value) || '=';
        const reg = /^(=)|(!=|！=)|(>)|(<)$/g;
        const finalSymbol = symbol.replace(reg, (_m, $1, $2, $3, $4) => {
            return ($1 && SymbolType.Equal) ||
                ($2 && SymbolType.NotEqual) ||
                ($3 && SymbolType.Greater) ||
                ($4 && SymbolType.Less) ||
                SymbolType.Equal;
        });
        if (range == null)
            return 0;
        if (rangeNode.valueType === types_1.BasicValueType.Array) {
            const filterTypes = [types_1.BasicValueType.String, types_1.BasicValueType.Number]; // special convertible type
            switch (finalSymbol) {
                case SymbolType.Equal: {
                    if (filterTypes.includes(rangeNode.innerValueType) && filterTypes.includes(conditionNode.valueType)) {
                        return range.filter(v => v == condition).length;
                    }
                    if (rangeNode.innerValueType !== conditionNode.valueType) {
                        return 0;
                    }
                    if (rangeNode.innerValueType === types_1.BasicValueType.DateTime && conditionNode.valueType === types_1.BasicValueType.DateTime) {
                        return range.filter(v => (0, dayjs_1.default)(v).valueOf() === (0, dayjs_1.default)(condition).valueOf()).length;
                    }
                    return range.filter(v => v === condition).length;
                }
                case SymbolType.NotEqual: {
                    if (filterTypes.includes(rangeNode.innerValueType) && filterTypes.includes(conditionNode.valueType)) {
                        return range.filter(v => v != condition).length;
                    }
                    if (rangeNode.innerValueType !== conditionNode.valueType) {
                        return range.length;
                    }
                    if (rangeNode.innerValueType === types_1.BasicValueType.DateTime && conditionNode.valueType === types_1.BasicValueType.DateTime) {
                        return range.filter(v => (0, dayjs_1.default)(v).valueOf() !== (0, dayjs_1.default)(condition).valueOf()).length;
                    }
                    return range.filter(v => v !== condition).length;
                }
                case SymbolType.Greater: {
                    if (filterTypes.includes(rangeNode.innerValueType) && filterTypes.includes(conditionNode.valueType)) {
                        return range.filter(v => v > condition).length;
                    }
                    if (rangeNode.innerValueType !== conditionNode.valueType) {
                        return 0;
                    }
                    if (rangeNode.innerValueType === types_1.BasicValueType.DateTime && conditionNode.valueType === types_1.BasicValueType.DateTime) {
                        return range.filter(v => (0, dayjs_1.default)(v).valueOf() > (0, dayjs_1.default)(condition).valueOf()).length;
                    }
                    return range.filter(v => v > condition).length;
                }
                case SymbolType.Less: {
                    if (filterTypes.includes(rangeNode.innerValueType) && filterTypes.includes(conditionNode.valueType)) {
                        return range.filter(v => v < condition).length;
                    }
                    if (rangeNode.innerValueType !== conditionNode.valueType) {
                        return 0;
                    }
                    if (rangeNode.innerValueType === types_1.BasicValueType.DateTime && conditionNode.valueType === types_1.BasicValueType.DateTime) {
                        return range.filter(v => (0, dayjs_1.default)(v).valueOf() < (0, dayjs_1.default)(condition).valueOf()).length;
                    }
                    return range.filter(v => v < condition).length;
                }
            }
        }
        else if (rangeNode.valueType === types_1.BasicValueType.String) {
            switch (finalSymbol) {
                case SymbolType.Equal: {
                    const filterTypes = [types_1.BasicValueType.String, types_1.BasicValueType.Number]; // special convertible type
                    if (filterTypes.includes(conditionNode.valueType)) {
                        return range.split(condition).length - 1;
                    }
                    return 0;
                }
                case SymbolType.NotEqual:
                    return 1;
                default:
                    return 0;
            }
        }
        return 0;
    }
}
exports.CountIf = CountIf;
//# sourceMappingURL=array.js.map